import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '@core/injectors';
import {
  BulkTableUpdateResponseDTO,
  BulkUpdateTypePayload,
  CategoryDTO,
  DataSourceBulkUpdateCategoryDTO,
  DataSourceBulkUpdateCategoryResponseDTO,
} from '@core/models';
import { sortObjectArrayByKey } from '@core/utils';
import { BulkTableUpdate } from '@features/connectors/models';
import { map, Observable } from 'rxjs';
import {
  DataSourceColumnItemDTO,
  DataSourceColumnItemUpdateDTO,
  DataSourceDataTypeMap,
  DataSourceItemDTO,
  DataSourceSchemaDTO,
  DataSourceSchemaHierarchicalDTO,
  DataSourceSchemaUpdateDTO,
  DataSourceTableDefinedKeysDTO,
  DataSourceTableItemDTO,
  DataSourceTableItemUpdateDTO,
  DataSourceTablePossibleForeignKeysDTO,
  DataSourceTableUpdateKeysDTO,
  DataSourceTableUpdateKeysResponceDTO,
  DataSourceUpgradeableDTO,
  PaginatedTablesResponse,
} from '../../models';

@Injectable()
export class DataSourceDataService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string,
  ) {}

  getList(): Observable<DataSourceItemDTO[]> {
    return this.http.get<DataSourceItemDTO[]>(`${this.apiBaseUrl}/api/data-sources/`).pipe(
      map((dataSources) => {
        return dataSources.map((dataSource) => {
          return {
            ...dataSource,
            categories: dataSource.categories ? sortObjectArrayByKey(dataSource.categories, 'name', true) : [],
          };
        });
      }),
    );
  }

  /**
   * Sources a new connector may read from. The server leaves out any source one of the user's
   * connector drafts already uses (PLAT-4444); `draftId` keeps that one draft's own source in the
   * list, for a resumed draft.
   */
  getResourceList(draftId?: number | null): Observable<DataSourceUpgradeableDTO[]> {
    const params: Record<string, string> = draftId != null ? { draft: String(draftId) } : {};
    return this.http.get<DataSourceUpgradeableDTO[]>(`${this.apiBaseUrl}/api/data-sources/upgradeable/`, { params });
  }

  getResourceItem(id: number): Observable<DataSourceUpgradeableDTO> {
    return this.http.get<DataSourceUpgradeableDTO>(`${this.apiBaseUrl}/api/data-sources/${id}/`);
  }

  getDataSourcesItem(id: number): Observable<DataSourceItemDTO> {
    return this.http.get<DataSourceItemDTO>(`${this.apiBaseUrl}/api/data-sources/${id}/`).pipe(
      map((dataSource) => {
        return {
          ...dataSource,
          categories: dataSource.categories ? sortObjectArrayByKey(dataSource.categories, 'name', true) : [],
        };
      }),
    );
  }

  getPossibleForeignKeys(tableId: number): Observable<DataSourceTablePossibleForeignKeysDTO[]> {
    return this.http.get<DataSourceTablePossibleForeignKeysDTO[]>(
      `${this.apiBaseUrl}/api/data-source-tables/${tableId}/possible_foreign_keys/`,
    );
  }

  getDataSourceTables(
    id: number,
    page: number,
    ordering: string,
    search: string = '',
  ): Observable<PaginatedTablesResponse> {
    return this.http
      .get<PaginatedTablesResponse>(`${this.apiBaseUrl}/api/data-sources/${id}/tables/`, {
        params: { page, ordering, search },
      })
      .pipe(
        map(({ count, next, previous, results }) => ({
          count,
          next,
          previous,
          results: results.map((table) => ({
            ...table,
            categories: table.categories ? sortObjectArrayByKey(table.categories, 'name', true) : [],
          })),
        })),
      );
  }

  updateCatalogForDataSource(id: number) {
    return this.http.patch<BulkTableUpdateResponseDTO>(`${this.apiBaseUrl}/api/data-sources/${id}/`, {
      connect_status: 'UPDATE_CATALOG',
    });
  }

  getDataSourceTableItem(id: number): Observable<DataSourceTableItemDTO> {
    return this.http.get<DataSourceTableItemDTO>(`${this.apiBaseUrl}/api/data-source-tables/${id}/`).pipe(
      map((table) => {
        return {
          ...table,
          categories: table.categories ? sortObjectArrayByKey(table.categories, 'name', true) : [],
        };
      }),
    );
  }

  getDataSourceTableColumns(id: number): Observable<DataSourceColumnItemDTO[]> {
    return this.http.get<DataSourceColumnItemDTO[]>(`${this.apiBaseUrl}/api/data-source-tables/${id}/columns/`).pipe(
      map((columns) => {
        return columns.map((column) => {
          return {
            ...column,
            categories: column.categories ? sortObjectArrayByKey(column.categories, 'name', true) : [],
          };
        });
      }),
    );
  }

  getDataSourceTableColumnsConverted(id: number): Observable<DataSourceColumnItemDTO[]> {
    return this.getDataSourceTableColumns(id).pipe(
      map((dataSourceColumn) => {
        return dataSourceColumn.map((column) => ({
          ...column,
          // todo: stored_data_type change to uppercase by default. use pipe to convert to camelCase
          stored_data_type:
            DataSourceDataTypeMap.get(column.stored_data_type?.toLowerCase()) ?? column.stored_data_type,
          data_type: DataSourceDataTypeMap.get(column.data_type?.toLowerCase()) ?? column.data_type,
        }));
      }),
    );
  }

  updateDataSourceTableItem(
    id: number,
    data: DataSourceTableItemUpdateDTO,
    internal_user_override: boolean,
  ): Observable<DataSourceTableItemDTO> {
    return this.http.patch<DataSourceTableItemDTO>(
      `${this.apiBaseUrl}/api/data-source-tables/${id}/${internal_user_override ? '?internal_user_override=true' : ''}`,
      data,
    );
  }

  updateDataSourceColumnItem(id: number, data: DataSourceColumnItemUpdateDTO): Observable<DataSourceColumnItemDTO> {
    return this.http.patch<DataSourceColumnItemDTO>(`${this.apiBaseUrl}/api/data-source-columns/${id}/`, data);
  }

  updateTablesDownload(data: BulkTableUpdate, internal_user_override: boolean): Observable<BulkTableUpdateResponseDTO> {
    return this.http.patch<BulkTableUpdateResponseDTO>(
      `${this.apiBaseUrl}/api/data-source-tables/bulk_download_update/${internal_user_override ? '?internal_user_override=true' : ''}`,
      data,
    );
  }

  getCategories(): Observable<CategoryDTO[]> {
    return this.http
      .get<CategoryDTO[]>(`${this.apiBaseUrl}/api/categories/`)
      .pipe(map((categories) => sortObjectArrayByKey(categories, 'name', true)));
  }

  getDefinedKeys(tableId: number): Observable<DataSourceTableDefinedKeysDTO[]> {
    return this.http.get<DataSourceTableDefinedKeysDTO[]>(
      `${this.apiBaseUrl}/api/data-source-tables/${tableId}/defined_keys/`,
    );
  }

  updateKeys(
    tableId: number,
    data: DataSourceTableUpdateKeysDTO[],
    pre_check: boolean,
  ): Observable<DataSourceTableUpdateKeysResponceDTO> {
    return this.http.put<DataSourceTableUpdateKeysResponceDTO>(
      `${this.apiBaseUrl}/api/data-source-tables/${tableId}/manage_keys/${pre_check ? '?pre_check=true' : ''}`,
      data,
    );
  }

  rediscoverDataSource(id: number): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.apiBaseUrl}/api/data-sources/${id}/retrigger_discovery/`, {});
  }

  bulkCategoryUpdate(data: DataSourceBulkUpdateCategoryDTO): Observable<DataSourceBulkUpdateCategoryResponseDTO> {
    return this.http.patch<DataSourceBulkUpdateCategoryResponseDTO>(
      `${this.apiBaseUrl}/api/data-source-columns/bulk_category_update/`,
      data,
    );
  }

  bulkUpdateColumnsType(payload: BulkUpdateTypePayload): Observable<DataSourceColumnItemDTO[]> {
    return this.http.patch<DataSourceColumnItemDTO[]>(
      `${this.apiBaseUrl}/api/data-source-columns/bulk-update-type/`,
      payload,
    );
  }

  getDataSourceSchemas(dataSourceId: number): Observable<DataSourceSchemaDTO[]> {
    return this.http.get<DataSourceSchemaDTO[]>(`${this.apiBaseUrl}/api/data-sources/${dataSourceId}/schemas/`).pipe(
      map((schemas) => {
        return schemas.map((schema) => ({
          ...schema,
          categories: schema.categories ? sortObjectArrayByKey(schema.categories, 'name', true) : [],
          selected: false, // Initialize selection state
        }));
      }),
    );
  }

  getDataSourceSchemasHierarchical(dataSourceId: number): Observable<DataSourceSchemaHierarchicalDTO[]> {
    return this.http
      .get<
        DataSourceSchemaHierarchicalDTO[]
      >(`${this.apiBaseUrl}/api/data-sources/${dataSourceId}/schemas-with-tables/`)
      .pipe(
        map((schemas) => {
          return schemas.map((schema) => ({
            ...schema,
            categories: schema.categories ? sortObjectArrayByKey(schema.categories, 'name', true) : [],
            tables: schema.tables.map((table) => ({
              ...table,
              categories: table.categories ? sortObjectArrayByKey(table.categories, 'name', true) : [],
            })),
          }));
        }),
      );
  }

  updateDataSourceSchema(schemaId: number, data: DataSourceSchemaUpdateDTO): Observable<DataSourceSchemaDTO> {
    return this.http.patch<DataSourceSchemaDTO>(`${this.apiBaseUrl}/api/data-source-schemas/${schemaId}/`, data);
  }

  bulkUpdateTables(
    dataSourceId: number,
    data: { table_ids: number[]; selected: boolean },
  ): Observable<BulkTableUpdateResponseDTO> {
    return this.http.patch<BulkTableUpdateResponseDTO>(
      `${this.apiBaseUrl}/api/data-sources/${dataSourceId}/bulk_update_tables/`,
      data,
    );
  }
}
